<template>
  <div class="modern-contact page-theme page-theme--lavender">
    <page-stripes side="right" :colors="stripeColors" />
    <section class="hero container">
      <div>
        <p class="eyebrow">CONTACT</p>
        <h1>kokono チームへのご相談</h1>
        <p class="lead">
          サービス導入、パートナーシップ、取材のご連絡などはこちらから。既存の
          `/contact` ページを温存しつつ、新 UI で動作検証が可能です。
        </p>
      </div>
      <div class="contact-methods">
        <article v-for="item in methods" :key="item.title">
          <p class="label">{{ item.title }}</p>
          <p class="value">{{ item.value }}</p>
          <small>{{ item.note }}</small>
        </article>
      </div>
    </section>

    <section class="section container form-section">
      <div class="section__header">
        <p class="eyebrow">MESSAGE</p>
        <h2>お問い合わせフォーム</h2>
      </div>
      <form class="form-card" @submit.prevent="validate">
        <label>
          お名前
          <input
            type="text"
            v-model="fullname.fnvalue"
            placeholder="山田 太郎"
          />
          <small v-if="fullname.fnerror">お名前は必須です。</small>
        </label>
        <label>
          メールアドレス
          <input
            type="email"
            v-model="email.emailvalue"
            placeholder="name@example.com"
          />
          <small v-if="email.emailerror"
            >正しいメールアドレスを入力してください。</small
          >
        </label>
        <label>
          件名
          <input type="text" v-model="subject.sjvalue" placeholder="ご用件" />
          <small v-if="subject.sjerror">件名は必須です。</small>
        </label>
        <label>
          詳細
          <textarea
            rows="5"
            v-model="message.msgvalue"
            placeholder="お問い合わせ内容を入力"
          ></textarea>
          <small v-if="message.msgerror">内容は必須です。</small>
        </label>
        <button class="btn primary" type="submit" :disabled="submitting">
          {{ submitting ? "送信中..." : "送信する" }}
        </button>
        <p class="status" v-if="status">{{ status }}</p>
      </form>
    </section>

    <section class="section container">
      <div class="section__header">
        <p class="eyebrow">FAQ</p>
        <h2>よくあるご質問</h2>
      </div>
      <div class="faq-grid">
        <article v-for="item in faq" :key="item.q">
          <p class="question">{{ item.q }}</p>
          <p class="answer">{{ item.a }}</p>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { reactive, toRefs, watch, ref } from "vue";
import axios from "axios";
import PageStripes from "../components/PageStripes.vue";

export default {
  name: "ModernContact",
  components: {
    PageStripes,
  },
  setup() {
    const stripeColors = ["#f3e8ff", "#e9d5ff", "#d8b4fe", "#c084fc"];
    const methods = ref([
      {
        title: "Email",
        value: "info@kokono.cloud",
        note: "24時間以内に折り返します",
      },
      {
        title: "所在地",
        value: "埼玉県蕨市北町1-25-12",
        note: "リモート中心で全国対応",
      },
      {
        title: "デモリクエスト",
        value: "https://www.kokono.cloud/",
        note: "フォーム送信後に調整します",
      },
    ]);

    const faq = ref([
      {
        q: "返答までの目安は？",
        a: "営業日で 1-2 日が目安です。緊急の場合は件名に [URGENT] を付けてください。",
      },
      {
        q: "フォーム送信ができない場合",
        a: "直接 info@kokono.cloud へご連絡ください。ログを確認いたします。",
      },
      {
        q: "オンラインミーティングは可能？",
        a: "はい。フォーム送信後に日程調整リンクを共有します。",
      },
    ]);

    const form = reactive({
      fullname: { fnvalue: "", fnerror: false },
      email: { emailvalue: "", emailerror: false },
      subject: { sjvalue: "", sjerror: false },
      message: { msgvalue: "", msgerror: false },
    });

    const submitting = ref(false);
    const status = ref("");

    const mailformat = new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

    watch(
      () => form.email.emailvalue,
      (val) => {
        if (val && !mailformat.test(val)) form.email.emailerror = true;
        else form.email.emailerror = false;
      }
    );

    function validate() {
      status.value = "";
      submitting.value = false;
      let ok = true;
      if (form.fullname.fnvalue === "") {
        form.fullname.fnerror = true;
        ok = false;
      } else form.fullname.fnerror = false;
      if (form.email.emailvalue === "" || form.email.emailerror) {
        form.email.emailerror = true;
        ok = false;
      }
      if (form.subject.sjvalue === "") {
        form.subject.sjerror = true;
        ok = false;
      } else form.subject.sjerror = false;
      if (form.message.msgvalue === "") {
        form.message.msgerror = true;
        ok = false;
      } else form.message.msgerror = false;

      if (!ok) return;
      submit();
    }

    async function submit() {
      submitting.value = true;
      try {
        const url = "http://localhost/api/send.php";
        const params = {
          name: form.fullname.fnvalue,
          email: form.email.emailvalue,
          subject: form.subject.sjvalue,
          message: form.message.msgvalue,
        };
        await axios.post(url, params);
        status.value = "送信が完了しました。担当より折り返します。";
        form.fullname.fnvalue = "";
        form.email.emailvalue = "";
        form.subject.sjvalue = "";
        form.message.msgvalue = "";
      } catch (error) {
        console.error(error);
        status.value = "送信に失敗しました。時間をおいて再度お試しください。";
      } finally {
        submitting.value = false;
      }
    }

    return {
      stripeColors,
      ...toRefs(form),
      methods,
      faq,
      submitting,
      status,
      validate,
    };
  },
};
</script>

<style lang="scss" scoped>
.modern-contact {
  background: #ffffff;
  color: #1e293b;
  min-height: 100vh;
  position: relative;
  z-index: 1;
}

.container {
  width: min(1180px, 92vw);
  margin: 0 auto;
}

.hero {
  padding: 5rem 0 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.eyebrow {
  letter-spacing: 0.4em;
  font-size: 0.8rem;
  color: var(--theme-accent);
}

h1 {
  color: #1e293b;
}

.lead {
  color: #64748b;
  line-height: 1.6;
}

.contact-methods {
  display: grid;
  gap: 1rem;
}

.contact-methods article {
  background: var(--theme-bg-alt);
  border-radius: 18px;
  border: 1px solid var(--theme-accent-pale);
  border-left: 3px solid var(--theme-accent-light);
  padding: 1.4rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.label {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.75rem;
  color: #64748b;
}

.value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
}

.section {
  padding: 3rem 0;
}

.section__header h2 {
  color: #1e293b;
}

.form-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-top: 3px solid var(--theme-accent-light);
  border-radius: 24px;
  padding: 2rem;
  display: grid;
  gap: 1.2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.06);
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-weight: 600;
  color: #1e293b;
}

input,
textarea {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.9rem 1rem;
  color: #1e293b;
  font-size: 1rem;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: var(--theme-accent);
}

small {
  color: #dc2626;
}

.btn {
  border-radius: 999px;
  padding: 0.9rem 1.8rem;
  border: none;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(
    120deg,
    var(--theme-accent-light),
    var(--theme-accent)
  );
  color: var(--theme-btn-text);
  transition: opacity 0.2s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status {
  font-size: 0.95rem;
  color: #64748b;
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
}

.faq-grid article {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--theme-accent-light);
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.question {
  font-weight: 600;
  color: #1e293b;
}

.answer {
  color: #64748b;
}
</style>
